import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import { usePaywall } from "@/components/PaywallContext";
import axios from "axios";
import { CheckCircle, Loader2, Crown, Calendar, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refreshUser } = useAuth();
  const { refreshTrialStatus } = usePaywall();
  const [status, setStatus] = useState("loading"); // loading, success, error
  const [message, setMessage] = useState("");

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        setStatus("error");
        setMessage("No session ID found");
        return;
      }

      try {
        // Poll for payment status
        let attempts = 0;
        const maxAttempts = 10;
        const pollInterval = 2000;

        const poll = async () => {
          attempts++;
          
          const response = await axios.get(
            `${API}/subscription/checkout-status/${sessionId}`,
            { withCredentials: true }
          );

          if (response.data.payment_status === "paid") {
            setStatus("success");
            setMessage("You're now in control of your finances!");
            // Refresh user data and paywall status to unlock premium features immediately
            if (refreshUser) {
              await refreshUser();
            }
            if (refreshTrialStatus) {
              await refreshTrialStatus();
            }
            return;
          }

          if (response.data.status === "expired" || attempts >= maxAttempts) {
            setStatus("error");
            setMessage("Payment verification timed out. Please check your account.");
            return;
          }

          // Continue polling
          setTimeout(poll, pollInterval);
        };

        await poll();
      } catch (error) {
        console.error("Error verifying payment:", error);
        setStatus("error");
        setMessage("Error verifying payment. Please check your account.");
      }
    };

    verifyPayment();
  }, [sessionId, refreshUser, refreshTrialStatus]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-gradient-to-b from-slate-800/80 to-slate-900/90 rounded-2xl shadow-2xl border border-slate-700/50 p-8 text-center backdrop-blur-sm">
          {status === "loading" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-emerald-500/10 rounded-full flex items-center justify-center">
                <Loader2 className="w-10 h-10 text-emerald-400 animate-spin" />
              </div>
              <h1 className="font-manrope text-2xl font-bold text-white mb-2">
                Setting up your account...
              </h1>
              <p className="text-slate-400">
                Please wait while we activate your premium features.
              </p>
            </>
          )}

          {status === "success" && (
            <>
              <div className="relative w-20 h-20 mx-auto mb-6">
                <div className="absolute inset-0 bg-emerald-500/20 rounded-full animate-ping" />
                <div className="relative w-20 h-20 bg-gradient-to-br from-emerald-400 to-emerald-600 rounded-full flex items-center justify-center shadow-lg shadow-emerald-500/30">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
              </div>
              
              <h1 className="font-manrope text-2xl font-bold text-white mb-2">
                You&apos;re Now in Control
              </h1>
              <p className="text-slate-400 mb-6">{message}</p>
              
              <div className="bg-slate-800/50 rounded-xl p-5 mb-6 border border-slate-700/50">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Crown className="w-5 h-5 text-amber-400" />
                  <span className="font-semibold text-white">Premium Features Unlocked</span>
                </div>
                <ul className="text-sm text-slate-300 space-y-3 text-left">
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span>Track all income and bills in one place</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span>See your financial future with forecasting</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span>Stay in control and avoid missed payments</span>
                  </li>
                  <li className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span>Reduce debt faster with visual tracking</span>
                  </li>
                </ul>
              </div>

              <Button
                onClick={() => navigate("/calendar")}
                className="w-full h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-xl shadow-lg shadow-emerald-500/25"
                data-testid="go-to-calendar-btn"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Start Using Premium
              </Button>
            </>
          )}

          {status === "error" && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 bg-amber-500/10 rounded-full flex items-center justify-center">
                <Crown className="w-10 h-10 text-amber-400" />
              </div>
              <h1 className="font-manrope text-2xl font-bold text-white mb-2">
                Verification Issue
              </h1>
              <p className="text-slate-400 mb-6">{message}</p>
              
              <div className="space-y-3">
                <Button
                  onClick={() => navigate("/settings")}
                  className="w-full h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-semibold rounded-xl"
                >
                  Check Account Status
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/calendar")}
                  className="w-full h-10 border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  Return to Calendar
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
